import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { useEffect, useCallback, useRef, useState, useMemo } from 'react'
import { useWebSocketContextSafe } from '../../real-time-updates/useWebSocketContext'
import { balancesApi } from '../api/balances.api'
import type { BalancesResponse, BalanceItem, AccountType } from '../types/balances.types'

const QUERY_KEY: QueryKey = ['balances']
const STALE_TIME = 2 * 60 * 1000
const REFETCH_INTERVAL = 5 * 60 * 1000
const RETRY_DELAY_BASE = 1000
const MAX_RETRY_DELAY = 30000

interface BalancesState {
    lastManualRefresh: number | null
    pendingUpdate: boolean
}

const globalState: BalancesState = {
    lastManualRefresh: null,
    pendingUpdate: false,
}

interface BalanceChangedMessage {
    organization: string
    type: AccountType
    currency: string
    amount: number
}

function isBalanceChangedMessage(data: unknown): data is BalanceChangedMessage {
    return (
        typeof data === 'object' &&
        data !== null &&
        'organization' in data &&
        'type' in data &&
        'currency' in data &&
        'amount' in data &&
        typeof (data as BalanceChangedMessage).organization === 'string' &&
        typeof (data as BalanceChangedMessage).type === 'string' &&
        typeof (data as BalanceChangedMessage).currency === 'string' &&
        typeof (data as BalanceChangedMessage).amount === 'number'
    )
}

export function useBalances() {
    const queryClient = useQueryClient()
    const wsContext = useWebSocketContextSafe()
    
    const [isManualRefreshing, setIsManualRefreshing] = useState(false)
    const [nextUpdateIn, setNextUpdateIn] = useState<number | null>(null)
    
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const broadcastRef = useRef<BroadcastChannel | null>(null)

    useEffect(() => {
        if (typeof window === 'undefined') return
        
        const channel = new BroadcastChannel('balances-sync')
        broadcastRef.current = channel
        
        channel.onmessage = (event) => {
            if (event.data?.type === 'BALANCES_REFRESHED') {
                queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            }
        }
        
        return () => channel.close()
    }, [queryClient])

    const {
        data: response,
        isLoading: isQueryLoading,
        isFetching,
        error,
        refetch,
        dataUpdatedAt,
        fetchStatus,
    } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: async () => {
            const result = await balancesApi.getBalances()
            globalState.pendingUpdate = false
            return result
        },
        refetchInterval: REFETCH_INTERVAL,
        refetchOnWindowFocus: true,
        staleTime: STALE_TIME,
        retry: (failureCount, error) => {
            const status = (error as { response?: { status: number } })?.response?.status
            if (status === 401 || status === 403) return false
            return failureCount < 3
        },
        retryDelay: (attemptIndex) => {
            const jitter = Math.random() * 1000
            return Math.min(RETRY_DELAY_BASE * Math.pow(2, attemptIndex), MAX_RETRY_DELAY) + jitter
        },
        networkMode: 'offlineFirst',
        placeholderData: (previousData) => previousData,
    })

    const data = useMemo(() => response?.data ?? [], [response])
    
    const hasData = useMemo(() => data.length > 0, [data])
    
    const isMockData = useMemo(() => {
        if (response?.isMock === true) return true
        if (!response?.updatedAt && hasData) return true
        if (balancesApi.isUsingMock()) return true
        return false;    
    }, [response])
    
    
    const isLoading = isQueryLoading && !hasData

    useEffect(() => {
        if (!dataUpdatedAt) {
            setNextUpdateIn(null)
            return
        }

        const updateCountdown = () => {
            const elapsed = Date.now() - dataUpdatedAt
            const remaining = Math.max(0, REFETCH_INTERVAL - elapsed)
            setNextUpdateIn(Math.ceil(remaining / 1000))
        
            if (remaining <= 0 && countdownRef.current) {
                clearInterval(countdownRef.current)
            }
        }

        updateCountdown()
        countdownRef.current = setInterval(updateCountdown, 1000)
        
        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current)
        }
    }, [dataUpdatedAt])

    useEffect(() => {
        if (!wsContext?.lastMessage) return

        const { lastMessage } = wsContext
        
        switch (lastMessage.type) {
        case 'balances_updated':
        case '1c_sync_completed':
            queryClient.invalidateQueries({ 
                queryKey: QUERY_KEY,
                exact: false,
            })
            break
            
        case 'balance_changed':
            if (isBalanceChangedMessage(lastMessage.data)) {
                const msgData = lastMessage.data
                
                queryClient.setQueryData<BalancesResponse>(QUERY_KEY, (old) => {
                    if (!old?.data) return old
                    
                    const updatedData = old.data.map((item: BalanceItem) => {
                        if (item.organization === msgData.organization && 
                            item.accountName === msgData.type &&
                            item.currency === msgData.currency) {
                            return { 
                                ...item, 
                                amount: msgData.amount, 
                                syncedAt: new Date().toISOString() 
                            }
                        }
                        return item
                    })
                    
                    return { 
                        ...old, 
                        data: updatedData, 
                        updatedAt: new Date().toISOString() 
                    }
                })
            }
            break
        }
    }, [wsContext?.lastMessage, queryClient, wsContext])

    const refresh = useCallback(async () => {
        if (isManualRefreshing) return
        
        const now = Date.now()
        if (globalState.lastManualRefresh && (now - globalState.lastManualRefresh) < 10000) {
            console.log('[useBalances] Refresh debounced')
            return
        }

        setIsManualRefreshing(true)
        globalState.lastManualRefresh = now
        globalState.pendingUpdate = true

        try {
            await refetch({ throwOnError: true })
            broadcastRef.current?.postMessage({ type: 'BALANCES_REFRESHED', timestamp: now })
        } catch (err) {
            console.error('[useBalances] Manual refresh failed:', err)
            throw err
        } finally {
            setIsManualRefreshing(false)
        }
    }, [refetch, isManualRefreshing])

    const forceRefresh = useCallback(async () => {
        setIsManualRefreshing(true)
        globalState.lastManualRefresh = Date.now()
        
        try {
            await refetch({ cancelRefetch: true, throwOnError: true })
        } finally {
            setIsManualRefreshing(false)
        }
    }, [refetch])

    const lastUpdateText = useCallback(() => {
        if (!dataUpdatedAt) return null
        
        const date = new Date(dataUpdatedAt)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffSecs = Math.floor(diffMs / 1000)
        const diffMins = Math.floor(diffSecs / 60)
        
        if (diffSecs < 5) return 'только что'
        if (diffSecs < 60) return `${diffSecs} сек назад`
        if (diffMins < 60) return `${diffMins} мин назад`
        
        return date.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
        })
    }, [dataUpdatedAt])

    const connectionStatus = useCallback((): 'fresh' | 'stale' | 'offline' | 'error' => {
        if (error) return 'error'
        if (!dataUpdatedAt) return 'offline'
        
        const age = Date.now() - dataUpdatedAt
        if (age > REFETCH_INTERVAL * 2) return 'offline'
        if (age > STALE_TIME) return 'stale'
        return 'fresh'
    }, [error, dataUpdatedAt])

    return {
        data,
        updatedAt: response?.updatedAt,
        hasData,           // ← добавлено: true если есть данные
        isMockData,        // ← добавлено: флаг демо-режима
        isLoading,         // ← изменено: false если есть данные
        isFetching: isFetching || isManualRefreshing,
        isManualRefreshing,
        error: error as Error | null,
        refresh,
        forceRefresh,
        lastUpdateText: lastUpdateText(),
        nextUpdateIn,
        connectionStatus: connectionStatus(),
        fetchStatus,
    }
}

/**
 * Хук для получения конкретного счёта по ключу
 */
export function useBalanceItem(organization: string, type: AccountType, currency: string) {
  const { data, isLoading, error } = useBalances()
  
  const item = data.find(
    (i: BalanceItem) => i.organization === organization && 
         i.accountName === type && 
         i.currency === currency
  )
  
  return {
    item,
    isLoading,
    error,
    exists: !!item,
  }
}

/**
 * Хук для истории изменений конкретного счёта
 */
export function useBalanceHistory(
  organization: string,
  type: AccountType,
  currency: string,
  options: { enabled?: boolean; limit?: number } = {}
) {
  const { enabled = true, limit = 30 } = options
  
  const {
    data: history,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['balances', 'history', organization, type, currency, limit],
    queryFn: () => balancesApi.getAccountHistory(organization, type, currency, limit),
    enabled: enabled && !!organization && !!type && !!currency,
    staleTime: 5 * 60 * 1000,
  })
  
  return {
    history: history ?? [],
    isLoading,
    error: error as Error | null,
    refresh: refetch,
  }
}

/**
 * @deprecated Используйте useBalances с WebSocketProvider
 */
export function useBalancesRealtime() {
  const queryClient = useQueryClient()
  const wsContext = useWebSocketContextSafe()

  useEffect(() => {
    if (!wsContext?.lastMessage) return

    try {
      const msg = wsContext.lastMessage
      
      if (msg.type === 'balances_updated' || msg.type === '1c_sync_completed') {
        queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      }
    } catch {
      // ignore
    }
  }, [wsContext?.lastMessage, queryClient])

  return { connected: !!wsContext?.isConnected }
}